import { motion } from "framer-motion";
import {
    Download as DownloadIcon,
    FileBox,
    Tag,
    ExternalLink,
    Puzzle,
    PackageOpen,
} from "lucide-react";
import { resolveMediaUrl } from "@/lib/api";
import { trackEvent } from "@/lib/analytics";
import { useLang } from "@/i18n/LangContext";
import { SkeletonTile } from "@/components/Skeleton";

export default function Addons({ addons = [], loading = false }) {
    const { t, lang } = useLang();
    const visible = (addons || []).filter((a) => a.enabled !== false);
    if (!loading && visible.length === 0) return null;

    return (
        <section
            id="dodatki"
            data-testid="addons-section"
            className="relative py-24 md:py-32 bg-[color:var(--bg-surface)]"
        >
            <div className="max-w-7xl mx-auto px-6 md:px-12">
                <div className="grid lg:grid-cols-12 gap-10 items-end mb-12">
                    <div className="lg:col-span-6">
                        <div className="label-eyebrow mb-4 flex items-center gap-2">
                            <Puzzle size={12} className="text-[#34d399]" /> {t("05 - Dodatki")}
                        </div>
                        <h2 className="font-display uppercase text-4xl md:text-5xl lg:text-6xl tracking-tighter text-white">
                            {lang === "en" ? (
                                t("Rozszerz swoją mapę")
                            ) : (
                                <>Rozszerz<br />swoją mapę</>
                            )}
                        </h2>
                    </div>
                    <p className="lg:col-span-6 text-base text-[color:var(--text-secondary)] max-w-xl">
                        {t("Oficjalne dodatki rozbudowujące świat Project Ruda Śląska o nowe tereny i lokalizacje. Każdy dodatek wymaga zainstalowanej mapy głównej.")}
                    </p>
                </div>

                <div className="grid gap-6 md:grid-cols-2">
                    {loading && visible.length === 0
                        ? Array.from({ length: 4 }).map((_, i) => (
                              <SkeletonTile key={i} className="h-full" />
                          ))
                        : visible.map((addon, i) => (
                              <AddonCard key={addon.id} addon={addon} index={i} />
                          ))}
                </div>
            </div>
        </section>
    );
}

function hexToRgba(hex, alpha) {
    let h = (hex || "#34d399").replace("#", "");
    if (h.length === 3) h = h.split("").map((c) => c + c).join("");
    const r = parseInt(h.substring(0, 2), 16) || 0;
    const g = parseInt(h.substring(2, 4), 16) || 0;
    const b = parseInt(h.substring(4, 6), 16) || 0;
    return `rgba(${r},${g},${b},${alpha})`;
}

function AddonCard({ addon, index }) {
    const { t } = useLang();
    const downloadHref = addon.download_url
        ? resolveMediaUrl(addon.download_url)
        : null;
    const isExternal = addon.download_type === "external";
    const accent = addon.accent_color || "#34d399";

    return (
        <motion.div
            data-testid={`addon-card-${addon.id}`}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.15 }}
            transition={{ delay: (index % 4) * 0.06, duration: 0.5 }}
            className="relative overflow-hidden flex flex-col rounded-xl"
            style={{
                background: "var(--bg-base)",
                border: `1px solid ${hexToRgba(accent, 0.3)}`,
            }}
        >
            {addon.image_url && (
                <div className="relative h-44 w-full overflow-hidden">
                    <img
                        src={resolveMediaUrl(addon.image_url)}
                        alt={addon.name}
                        loading="lazy"
                        decoding="async"
                        className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-[color:var(--bg-base)] via-[color:var(--bg-base)]/30 to-transparent" />
                </div>
            )}

            <div className="p-8 md:p-10 flex flex-col flex-1 relative">
                <div
                    className="absolute -top-20 -right-20 w-72 h-72 rounded-full blur-3xl pointer-events-none"
                    style={{ background: hexToRgba(accent, 0.1) }}
                />

                <div className="flex items-start justify-between mb-6 relative gap-4">
                    <div>
                        <div
                            className="text-[10px] uppercase tracking-[0.3em] mb-1.5"
                            style={{ color: accent }}
                        >
                            {t("Dodatek")}
                        </div>
                        <h3 className="font-display uppercase text-2xl md:text-3xl text-white tracking-tight">
                            {addon.name}
                        </h3>
                    </div>
                    <Puzzle
                        size={28}
                        strokeWidth={1.4}
                        style={{ color: accent }}
                        className="shrink-0"
                    />
                </div>

                <div className="grid grid-cols-2 gap-5 mb-7 relative">
                    <Stat icon={Tag} label="Wersja" value={addon.version || "-"} color={accent} />
                    <Stat icon={FileBox} label="Rozmiar" value={addon.file_size || "-"} color={accent} />
                </div>

                {addon.description && (
                    <p className="text-sm text-[color:var(--text-secondary)] leading-relaxed mb-6 relative whitespace-pre-wrap">
                        {addon.description}
                    </p>
                )}

                <div className="relative mt-auto">
                    {downloadHref ? (
                        <a
                            data-testid={`addon-card-${addon.id}-btn`}
                            href={downloadHref}
                            target={isExternal ? "_blank" : undefined}
                            rel={isExternal ? "noopener noreferrer" : undefined}
                            download={!isExternal}
                            onClick={() => trackEvent("addon_download_click", { label: addon.id || addon.name || "" })}
                            className="inline-flex items-center justify-center gap-2 px-7 py-4 font-semibold uppercase tracking-wider text-sm transition-all duration-300 w-full rounded-[4px]"
                            style={{ background: accent, color: "var(--bg-base)", border: `1px solid ${accent}` }}
                            onMouseEnter={(e) => {
                                e.currentTarget.style.transform = "translateY(-2px)";
                                e.currentTarget.style.boxShadow = `0 8px 30px ${accent}55`;
                            }}
                            onMouseLeave={(e) => {
                                e.currentTarget.style.transform = "translateY(0)";
                                e.currentTarget.style.boxShadow = "none";
                            }}
                        >
                            <DownloadIcon size={18} /> {t("Pobierz dodatek")}
                            {isExternal && <ExternalLink size={14} />}
                        </a>
                    ) : (
                        <div
                            data-testid={`addon-card-${addon.id}-unavailable`}
                            className="text-center py-4 text-[color:var(--text-secondary)] border border-dashed border-white/10 rounded-md text-sm flex items-center justify-center gap-2"
                        >
                            <PackageOpen size={16} /> {t("Plik do pobrania nie jest jeszcze dostępny.")}
                        </div>
                    )}
                </div>

                {addon.requirements && (
                    <div className="mt-7 relative">
                        <div
                            className="text-xs font-semibold uppercase tracking-[0.25em] mb-2"
                            style={{ color: accent }}
                        >
                            {t("Wymagania")}
                        </div>
                        <p className="text-sm text-[color:var(--text-secondary)] leading-relaxed">
                            {t(addon.requirements)}
                        </p>
                    </div>
                )}

                {addon.changelog && (
                    <div className="mt-6 relative">
                        <div
                            className="text-xs font-semibold uppercase tracking-[0.25em] mb-2"
                            style={{ color: accent }}
                        >
                            {t("Changelog")}
                        </div>
                        <pre className="text-sm text-[color:var(--text-secondary)] leading-relaxed whitespace-pre-wrap font-sans">
                            {t(addon.changelog)}
                        </pre>
                    </div>
                )}
            </div>
        </motion.div>
    );
}

function Stat({ icon: Icon, label, value, color }) {
    const { t } = useLang();
    return (
        <div>
            <Icon size={18} className="mb-2" strokeWidth={1.5} style={{ color }} />
            <div className="text-[10px] uppercase tracking-[0.25em] text-[color:var(--text-muted)]">
                {t(label)}
            </div>
            <div className="font-display text-xl text-white">{value}</div>
        </div>
    );
}
